import type { WishlistProductData } from '@/composables/useWishlist';
import { useWishlistStore } from '@/stores/pages/WishlistStore';
import { useProductStore } from '@/stores/products/ProductStore';
import type { AppPageProps } from '@/types';
import type { ConfigurableProductProductObject } from '@/types/generated';
import { ImageTypeEnum, resolvedImageUrl } from '@/utils/imageUrl';
import type { Page } from '@inertiajs/core';
import _ from 'lodash';
import { computed, watch, type ComputedRef, type WatchHandle } from 'vue';

/**
 * The product page's selected size, as the wishlist saves it.
 */
export function useProductWishlistComposable(): ComputedRef<WishlistProductData> {
    const productStore = useProductStore();

    return computed((): WishlistProductData => {
        const firstImage = productStore.selectedProduct.images[0];

        return {
            selectedSku: productStore.selectedProduct.product.sku,
            configurableProductSku: productStore.configurableProduct.group_identifier,
            urlKey: productStore.configurableProduct.url_key,
            productName: productStore.configurableProduct.name,
            price: productStore.selectedProduct.product.data.product_price ?? 0,
            thumbnail: firstImage ? (resolvedImageUrl(firstImage, ImageTypeEnum.Thumb) ?? null) : null,
            recommendedPrice: productStore.selectedProduct.product.data.product_price_recommended ?? null,
            discount: productStore.configurableProduct.discount,
        };
    });
}

/**
 * Opens the product page on a size saved on the wishlist, once the deferred wishlist has loaded.
 * A force_size_selection product without a chosen size keeps its default: its heart shows any saved size instead.
 * Returns the watcher's stop handle while it is still waiting.
 */
export function selectSavedWishlistSize(page: Page<AppPageProps>): WatchHandle | undefined {
    const restoredSku = useProductStore().selectedProduct.product.sku;

    if (page.props.wishlist_items !== undefined) {
        applySavedWishlistSize(restoredSku);

        return undefined;
    }

    return watch(
        () => page.props.wishlist_items,
        () => applySavedWishlistSize(restoredSku),
        { once: true },
    );
}

/**
 * A size the visitor already has (picked, from an earlier visit or a Mediaclip session), a filter changed while the wishlist loaded
 * or url filters outrank the saved one.
 */
function applySavedWishlistSize(restoredSku: string): void {
    const productStore = useProductStore();
    const hasChangedSize = productStore.selectedProduct.product.sku !== restoredSku;

    if (hasChangedSize || productStore.hasUserSelectedProduct || productStore.forceSizeSelectionActive || !_.isEmpty(productStore.urlFilters)) {
        return;
    }

    const savedSku = useWishlistStore().savedItemFor(productStore.configurableProduct.group_identifier)?.selected_sku;
    const savedProduct = _.find(
        productStore.configurableProduct.configurable_product_products,
        (configurableProductProduct: ConfigurableProductProductObject) => configurableProductProduct.product.sku === savedSku,
    );

    if (!savedProduct) {
        return;
    }

    productStore.presetProduct(savedProduct);
}
